import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  DialogActions,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import { lessonsAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from './UIKit/Button';
import TextField from './UIKit/TextField';
import Accordion from './UIKit/Accordion';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { formatDate } from '../utils/formatters';
import ResponsivePreviewModal from './ResponsivePreviewModal';
import CustomIframe from './UIKit/CustomIframe';

// Функція для відео прев'ю (як в EditLesson)
const renderVideoPreview = (videoUrl, height = '200px') => {
  // Обробка випадків, коли videoUrl є JSON об'єктом або рядком
  let actualVideoUrl = videoUrl;
  
  if (typeof videoUrl === 'string') {
    try {
      const parsed = JSON.parse(videoUrl);
      if (parsed && typeof parsed === 'object' && parsed.videoUrl) {
        actualVideoUrl = parsed.videoUrl;
      }
    } catch (e) {
      // Якщо не вдалося розпарсити JSON, використовуємо оригінальний рядок
      actualVideoUrl = videoUrl;
    }
  } else if (typeof videoUrl === 'object' && videoUrl && videoUrl.videoUrl) {
    actualVideoUrl = videoUrl.videoUrl;
  }
  
  // Функция для получения встраиваемого URL на основе обычного URL
  const getEmbedUrl = (url) => {
    if (!url) return '';

    // YouTube
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/)(\d+)/i;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // Если не удалось преобразовать URL, возвращаем исходный
    return url;
  };

  // Проверяем, является ли URL действительным URL видео
  const isValidVideoUrl = (url) => {
    if (!url) return false;
    return /youtube\.com|youtu\.be|vimeo\.com|mp4$|webm$|ogg$/i.test(url);
  };

  const embedUrl = getEmbedUrl(actualVideoUrl);
  
  if (!isValidVideoUrl(actualVideoUrl)) {
    return (
      <Box sx={{ 
        width: '100%', 
        height: height, 
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1
      }}>
        <Typography variant="body2" color="text.secondary">
          Video: {actualVideoUrl}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', pb: '56.25%', height: 0 }}>
      <iframe
        src={embedUrl}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          borderRadius: '8px',
        }}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Video Preview"
      />
    </Box>
  );
};

const LessonChangeRequestModal = ({ open, onClose, requestId, onRequestProcessed }) => {
  const { t } = useTranslation(['admin', 'common']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [changeRequest, setChangeRequest] = useState(null);
  const [originalLesson, setOriginalLesson] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [responsivePreviewOpen, setResponsivePreviewOpen] = useState(false);
  const [responsivePreviewLesson, setResponsivePreviewLesson] = useState(null);
  const [responsivePreviewTitle, setResponsivePreviewTitle] = useState('');
  const [selectedBlock, setSelectedBlock] = useState(null);

  // Функція для очищення всього state
  const resetState = useCallback(() => {
    setLoading(false);
    setSaving(false);
    setError(null);
    setChangeRequest(null);
    setOriginalLesson(null);
    setAdminComment('');
    setSelectedFields([]);
    setConfirmAction(null);
    setConfirmDialogOpen(false);
    setResponsivePreviewOpen(false);
    setResponsivePreviewLesson(null);
    setResponsivePreviewTitle('');
    setSelectedBlock(null);
  }, []);

  // Загрузка данных запроса на изменение
  const fetchChangeRequest = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await lessonsAPI.getChangeRequestById(requestId);
      
      if (response && response.data) {
        const requestData = response.data.data;
        setChangeRequest(requestData);
        setOriginalLesson(requestData.lesson);
        
        // Инициализировать выбранные поля в зависимости от статуса запроса
        if (requestData.status === 'approved' && requestData.approvedFields?.length > 0) {
          setSelectedFields([...requestData.approvedFields]);
        } else if (requestData.status === 'pending' && requestData.modifiedFields) {
          setSelectedFields([...requestData.modifiedFields]);
        } else if (requestData.modifiedFields) {
          setSelectedFields([]);
        }
      } else {
        setError(t('admin:changeRequestDetail.errors.notFound'));
      }
    } catch (err) {
      console.error('Ошибка при загрузке запроса на изменение:', err);
      setError(
        err.response?.data?.message || t('admin:changeRequestDetail.errors.loadError')
      );
    } finally {
      setLoading(false);
    }
  }, [requestId, t]);

  useEffect(() => {
    if (open && requestId) {
      fetchChangeRequest();
    } else if (!open) {
      // Очищаємо state коли модалка закривається
      resetState();
    }
  }, [open, requestId, fetchChangeRequest, resetState]);

  // Обработчик выбора/снятия поля
  const handleFieldToggle = useCallback((field) => {
    if (selectedFields.includes(field)) {
      setSelectedFields(selectedFields.filter(f => f !== field));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  }, [selectedFields]);

  // Обработчики для подтверждения/отклонения запроса
  const handleApproveClick = useCallback(() => {
    setConfirmAction('approve');
    setConfirmDialogOpen(true);
  }, []);

  const handleRejectClick = useCallback(() => {
    setConfirmAction('reject');
    setConfirmDialogOpen(true);
  }, []);

  // Обработчик обработки запроса (подтверждение/отклонение)
  const handleProcessRequest = useCallback(async () => {
    try {
      setSaving(true);
      setConfirmDialogOpen(false);

      const status = confirmAction === 'approve' ? 'approved' : 'rejected';
      
      await lessonsAPI.processChangeRequest(requestId, {
        status,
        adminComment,
        approvedFields: status === 'approved' ? selectedFields : []
      });

      toast.success(
        status === 'approved'
          ? t('admin:changeRequestDetail.messages.approveSuccess')
          : t('admin:changeRequestDetail.messages.rejectSuccess')
      );

      // Обновляем данные запроса
      await fetchChangeRequest();
      
      // Уведомляем родительский компонент об изменении
      if (onRequestProcessed) {
        onRequestProcessed();
      }

      // Очищаємо state після успішного оброблення
      resetState();
    } catch (err) {
      console.error('Ошибка при обработке запроса:', err);
      toast.error(
        err.response?.data?.message || t('admin:changeRequestDetail.errors.processingFailed')
      );
    } finally {
      setSaving(false);
    }
  }, [confirmAction, requestId, adminComment, selectedFields, t, fetchChangeRequest, onRequestProcessed, resetState]);

  const handleCancelDialog = useCallback(() => {
    setConfirmDialogOpen(false);
    setConfirmAction(null);
  }, []);

  // Функция для получения значения поля из объекта урока
  const getFieldValue = useCallback((lesson, field) => {
    if (!lesson) return '';    
    // Для простых полей возвращаем значение напрямую
    if (['title', 'content', 'videoUrl', 'duration', 'type', 'contentType'].includes(field)) {
      const value = lesson[field] || '';
      return value;
    }
    
    // Для contentBlocks показываем только контент, игнорируя технические поля
    if (field === 'contentBlocks') {
      const contentBlocks = lesson[field] || [];
      if (Array.isArray(contentBlocks)) {
        const contentOnly = contentBlocks.map(block => ({
          content: block.content || '',
          type: block.type || ''
        }));
        return JSON.stringify(contentOnly, null, 2);
      }
      return JSON.stringify(contentBlocks, null, 2);
    }
    
    // Для других сложных полей возвращаем JSON строку
    if (['resources', 'practiceExercises'].includes(field)) {
      return JSON.stringify(lesson[field] || [], null, 2);
    }
    
    return '';
  }, []);


  // Функция для создания предварительного просмотра урока с предлагаемыми изменениями
  const createPreviewLesson = useCallback(() => {
    if (!originalLesson || !changeRequest?.proposedChanges) {
      return originalLesson;
    }

    // Создаем копию оригинального урока
    const previewLesson = { ...originalLesson };

    // Применяем предлагаемые изменения
    if (changeRequest.proposedChanges.title) {
      previewLesson.title = changeRequest.proposedChanges.title;
    }
    if (changeRequest.proposedChanges.content) {
      previewLesson.content = changeRequest.proposedChanges.content;
    }
    if (changeRequest.proposedChanges.duration) {
      previewLesson.duration = changeRequest.proposedChanges.duration;
    }
    if (changeRequest.proposedChanges.type) {
      previewLesson.type = changeRequest.proposedChanges.type;
    }
    if (changeRequest.proposedChanges.contentType) {
      previewLesson.contentType = changeRequest.proposedChanges.contentType;
    }
    if (changeRequest.proposedChanges.videoUrl) {
      previewLesson.videoUrl = changeRequest.proposedChanges.videoUrl;
    }
    if (changeRequest.proposedChanges.contentBlocks) {
      previewLesson.contentBlocks = changeRequest.proposedChanges.contentBlocks;
    }
    if (changeRequest.proposedChanges.resources) {
      previewLesson.resources = changeRequest.proposedChanges.resources;
    }
    if (changeRequest.proposedChanges.practiceExercises) {
      previewLesson.practiceExercises = changeRequest.proposedChanges.practiceExercises;
    }

    return previewLesson;
  }, [originalLesson, changeRequest?.proposedChanges]);

  // Функція для рендерингу окремих блоків контенту
  const renderContentBlocksDiff = useCallback(() => {
    const originalBlocks = originalLesson?.contentBlocks || [];
    const newBlocks = changeRequest?.proposedChanges?.contentBlocks || [];
    const selectedBlocks = changeRequest?.selectedBlocks || [];
    
    // Для старих запитів (без selectedBlocks) показуємо всі блоки
    const isOldRequest = selectedBlocks.length === 0;
    
    // Знаходимо всі унікальні блоки (оригінальні + нові)
    const allBlocks = [];
    const maxLength = Math.max(originalBlocks.length, newBlocks.length);
    
    for (let i = 0; i < maxLength; i++) {
      const originalBlock = originalBlocks[i];
      const newBlock = newBlocks[i];
      const blockId = `contentBlock_${i}`;
      
      if (isOldRequest) {
        // Для старих запитів показуємо всі блоки
        if (originalBlock || newBlock) {
          allBlocks.push({
            index: i,
            original: originalBlock,
            new: newBlock,
            isChanged: !originalBlock || !newBlock || 
                      originalBlock.content !== newBlock.content || 
                      originalBlock.type !== newBlock.type
          });
        }
      } else {
        // Перевіряємо, чи вибрано цей блок
        const isBlockSelected = selectedBlocks.includes(blockId);
        
        if ((originalBlock || newBlock) && isBlockSelected) {
          allBlocks.push({
            index: i,
            original: originalBlock,
            new: newBlock,
            isChanged: !originalBlock || !newBlock || 
                      originalBlock.content !== newBlock.content || 
                      originalBlock.type !== newBlock.type
          });
        }
      }
    }
    
    return (
      <Box>
        {allBlocks.map(({ index, original, new: newBlock, isChanged }) => {
          const blockId = `contentBlock_${index}`;
          const isApproved = changeRequest?.status === 'approved' && 
                             changeRequest?.approvedFields?.includes(blockId);
          const isRejected = changeRequest?.status === 'approved' && 
                             !changeRequest?.approvedFields?.includes(blockId);
          
          return (
            <Box key={blockId} sx={{ mt: 2, mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedFields.includes(blockId)}
                    onChange={() => handleFieldToggle(blockId)}
                    disabled={changeRequest?.status !== 'pending'}
                    color={isApproved ? "success" : isRejected ? "error" : "primary"}
                  />
                }
                label={
                  <Box component="span" sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    color: isApproved ? "success.main" : isRejected ? "error.main" : "text.primary"
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Block #{index + 1} ({newBlock?.type || original?.type || 'unknown'})
                    </Typography>
                    {isApproved && (
                      <Chip 
                        size="small" 
                        color="success" 
                        label={t('admin:changeRequestDetail.approved')} 
                        sx={{ ml: 1 }} 
                      />
                    )}
                    {isRejected && (
                      <Chip 
                        size="small" 
                        color="error" 
                        label={t('admin:changeRequestDetail.rejected')} 
                        sx={{ ml: 1 }} 
                      />
                    )}
                  </Box>
                }
              />
              
              {/* Детальний контент блоку */}
              <Accordion 
                elevation={0} 
                variant="outlined" 
                sx={{ 
                  mt: 2,
                  borderRadius: '16px',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: '16px',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('admin:changeRequestDetail.preview.viewDetails')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  {/* ORIGINAL - оригінальний контент блоку + прев'ю */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ mb: 2 }}>
                      {t('admin:changeRequestDetail.preview.beforeChanges')}
                    </Typography>
                    <Grid container spacing={2} alignItems="stretch">
                      {/* Ліва частина: Оригінальний контент блоку */}
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin:changeRequestDetail.preview.originalText')}:
                          </Typography>
                        </Box>
                        <Paper 
                          elevation={0}
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            borderRadius: '16px',
                            bgcolor: (theme) => theme.palette.background.default,
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                            flex: 1,
                            minHeight: '200px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.5)',
                              },
                            },
                          }}
                        >
                          {original ? (
                            original.type === 'video' ? (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {original.content}
                              </Typography>
                            ) : original.type === 'html' ? (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {original.content}
                              </Typography>
                            ) : original.type === 'customHtml' ? (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {original.content}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {original.content}
                              </Typography>
                            )
                          ): null}
                        </Paper>
                      </Grid>
                      
                      {/* Права частина: Прев'ю оригінального блоку */}
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin:changeRequestDetail.preview.howItLooks')}:
                          </Typography>
                          {(original?.type === 'html' || original?.type === 'customHtml') && (
                            <Tooltip title="Responsive Preview">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenResponsivePreview(
                                  originalLesson,
                                  `Block #${index + 1} - ${t('admin:changeRequestDetail.preview.beforeChanges')}`,
                                  original
                                )}
                                sx={{ color: 'primary.main' }}
                              >
                                <SmartphoneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Paper
                          elevation={0}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: (theme) => theme.palette.background.default,
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                            flex: 1,
                            minHeight: '200px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.5)',
                              },
                            },
                          }}
                        >
                          {original ? (
                            original.type === 'video' ? (
                              renderVideoPreview(original.content, '200px')
                            ) : original.type === 'html' ? (
                              <Box sx={{ 
                                width: '100%', 
                                height: '200px', 
                                border: '1px solid #ddd', 
                                borderRadius: '8px',
                                overflow: 'auto'
                              }}>
                                <iframe
                                  title="HTML Preview"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                  }}
                                  srcDoc={original.content || ''}
                                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                                />
                              </Box>
                            ) : original.type === 'customHtml' ? (
                              <iframe
                                title="Custom HTML Preview"
                                style={{ width: '100%', height: '200px', border: '1px solid #ddd', borderRadius: '8px' }}
                                srcDoc={original.content || ''}
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                              />
                            ) : (
                              <Typography variant="body2">
                                {original.content}
                              </Typography>
                            )
                          ) : null}
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* AFTER CHANGES - новий контент блоку + прев'ю */}
                  <Box>
                    <Typography variant="h6" gutterBottom color="success.main" sx={{ mb: 2 }}>
                      {t('admin:changeRequestDetail.preview.afterChanges')}
                    </Typography>
                    <Grid container spacing={2} alignItems="stretch">
                      {/* Ліва частина: Новий контент блоку */}
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin:changeRequestDetail.preview.proposedText')}:
                          </Typography>
                        </Box>
                        <Paper 
                          elevation={0}
                          variant="outlined" 
                          sx={{ 
                            p: 2, 
                            borderRadius: '16px',
                            bgcolor: 'background.default',
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                            flex: 1,
                            minHeight: '200px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.5)',
                              },
                            },
                          }}
                        >
                          {newBlock ? (
                            newBlock.type === 'video' ? (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {newBlock.content}
                              </Typography>
                            ) : newBlock.type === 'html' ? (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {newBlock.content}
                              </Typography>
                            ) : newBlock.type === 'customHtml' ? (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {newBlock.content}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ 
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}>
                                {newBlock.content}
                              </Typography>
                            )
                          ) :null}
                        </Paper>
                      </Grid>
                      
                      {/* Права частина: Прев'ю нового блоку */}
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('admin:changeRequestDetail.preview.howItWillLook')}:
                          </Typography>
                          {(newBlock?.type === 'html' || newBlock?.type === 'customHtml') && (
                            <Tooltip title="Responsive Preview">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenResponsivePreview(
                                  createPreviewLesson(),
                                  `Block #${index + 1} - ${t('admin:changeRequestDetail.preview.afterChanges')}`,
                                  newBlock
                                )}
                                sx={{ color: 'success.main' }}
                              >
                                <SmartphoneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Paper
                          elevation={0}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: 'background.default',
                            display: 'flex',
                            flexDirection: 'column',
                            boxSizing: 'border-box',
                            flex: 1,
                            minHeight: '200px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.3)',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.5)',
                              },
                            },
                          }}
                        >
                          {newBlock ? (
                            newBlock.type === 'video' ? (
                              renderVideoPreview(newBlock.content, '200px')
                            ) : newBlock.type === 'html' ? (
                              <Box sx={{ 
                                width: '100%', 
                                height: '200px', 
                                border: '1px solid #ddd', 
                                borderRadius: '8px',
                                overflow: 'auto'
                              }}>
                                <iframe
                                  title="HTML Preview"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    border: 'none'
                                  }}
                                  srcDoc={newBlock.content || ''}
                                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                                />
                              </Box>
                            ) : newBlock.type === 'customHtml' ? (
                              <CustomIframe
                                title="Custom HTML Preview"
                                style={{ width: '100%', height: '200px', border: '1px solid #ddd', borderRadius: '8px' }}
                                srcDoc={newBlock.content || ''}
                                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                              />
                            ) : (
                              <Typography variant="body2">
                                {newBlock.content}
                              </Typography>
                            )
                          ) : null}                           
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </Box>
    );
  }, [originalLesson, changeRequest?.proposedChanges, changeRequest?.status, changeRequest?.approvedFields, selectedFields, t, handleFieldToggle, createPreviewLesson]);

    // Функція для відображення зміненого поля
  const renderFieldDiff = useCallback((field) => {
    // Для старих запитів (без selectedBlocks) показуємо всі поля
    const selectedBlocks = changeRequest?.selectedBlocks || [];
    const isOldRequest = selectedBlocks.length === 0;
    
    // Для старих запитів показуємо всі поля, для нових - тільки вибрані
    const isFieldSelected = isOldRequest || 
                           selectedBlocks.includes(field) || 
                           selectedBlocks.some(blockId => blockId.startsWith('contentBlock_') && field === 'contentBlocks') ||
                           selectedBlocks.includes('main_content') && (field === 'content' || field === 'contentType');
    
    // Якщо поле не вибрано, не відображаємо його
    if (!isFieldSelected) {
      return null;
    }
  
    const originalValue = getFieldValue(originalLesson, field);
    const newValue = getFieldValue(changeRequest?.proposedChanges, field);
        
    // Визначаємо статус заздалегідь для всіх полів
    const isApproved = changeRequest?.status === 'approved' && 
                       changeRequest?.approvedFields?.includes(field);
    
    const isRejected = changeRequest?.status === 'approved' && 
                       !changeRequest?.approvedFields?.includes(field);
    
    // Якщо це contentBlocks, рендеримо кожен блок окремо
    if (field === 'contentBlocks') {
      return renderContentBlocksDiff();
    }
    
    // Спеціальний випадок для contentType - лише два контейнери
    if (field === 'contentType') {
      return (
        <Box sx={{ mt: 2, mb: 2 }} key={field}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedFields.includes(field)}
                onChange={() => handleFieldToggle(field)}
                disabled={changeRequest?.status !== 'pending'}
                color={isApproved ? "success" : isRejected ? "error" : "primary"}
              />
            }
            label={
              <Box component="span" sx={{ 
                display: 'flex',
                alignItems: 'center',
                color: isApproved ? "success.main" : isRejected ? "error.main" : "text.primary"
              }}>
                {t(`admin:changeRequestDetail.fields.${field}`)}
                {isApproved && (
                  <Chip 
                    size="small" 
                    color="success" 
                    label={t('admin:changeRequestDetail.approved')} 
                    sx={{ ml: 1 }} 
                  />
                )}
                {isRejected && (
                  <Chip 
                    size="small" 
                    color="error" 
                    label={t('admin:changeRequestDetail.rejected')} 
                    sx={{ ml: 1 }} 
                  />
                )}
              </Box>
            }
          />
          
          {/* Селектор для перегляду типів */}
          <Accordion 
            elevation={0} 
            variant="outlined" 
            sx={{ 
              mt: 2,
              borderRadius: '16px',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderRadius: '16px',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {t('admin:changeRequestDetail.preview.viewDetails')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2 }}>
              {/* Два контейнери для contentType */}
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {/* Original Type */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        {t('admin:changeRequestDetail.diff.original')}:
                      </Typography>
                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: '8px',
                          bgcolor: (theme) => theme.palette.background.paper,
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          minHeight: '80px',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Typography variant="body1" sx={{ 
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {originalValue || 'standard'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                  
                  {/* Proposed Type */}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        {t('admin:changeRequestDetail.diff.proposed')}:
                      </Typography>
                      <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: '8px',
                          bgcolor: (theme) => theme.palette.background.paper,
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'flex-start',
                          minHeight: '80px',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Typography variant="body1" sx={{ 
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {newValue || 'standard'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      );
    }
    
    // Для інших полів - стандартна логіка
    
    return (
      <Box sx={{ mt: 2, mb: 2 }} key={field}>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedFields.includes(field)}
              onChange={() => handleFieldToggle(field)}
              disabled={changeRequest?.status !== 'pending'}
              color={isApproved ? "success" : isRejected ? "error" : "primary"}
            />
          }
          label={
            <Box component="span" sx={{ 
              display: 'flex',
              alignItems: 'center',
              color: isApproved ? "success.main" : isRejected ? "error.main" : "text.primary"
            }}>
              {t(`admin:changeRequestDetail.fields.${field}`)}
              {isApproved && (
                <Chip 
                  size="small" 
                  color="success" 
                  label={t('admin:changeRequestDetail.approved')} 
                  sx={{ ml: 1 }} 
                />
              )}
              {isRejected && (
                <Chip 
                  size="small" 
                  color="error" 
                  label={t('admin:changeRequestDetail.rejected')} 
                  sx={{ ml: 1 }} 
                />
              )}
            </Box>
          }
        />
        
        {/* Детальний контент всередині селектора - як у Lesson Preview */}
        <Accordion 
          elevation={0} 
          variant="outlined" 
          sx={{ 
            mt: 2,
            borderRadius: '16px',
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              borderRadius: '16px',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {t('admin:changeRequestDetail.preview.viewDetails')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            {/* ORIGINAL - оригінальний контент + прев'ю (50% + 50%) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom color="primary.main" sx={{ mb: 2 }}>
                {t('admin:changeRequestDetail.preview.beforeChanges')}
                </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {/* Ліва частина: Оригінальний контент (50%) */}
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin:changeRequestDetail.preview.originalText')}:
                    </Typography>
                  </Box>
                <Paper 
                    elevation={0}
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                      borderRadius: '16px',
                      bgcolor: (theme) => theme.palette.background.default,
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                      flex: 1,
                      minHeight: '300px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.5)',
                        },
                      },
                    }}
                  >
                    {field === 'content' && (originalLesson?.contentType === 'presentation' || originalLesson?.contentType === 'htmlBlock') ? (
                      (() => {
                        return (
                          <Typography variant="body2" sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {originalValue}
                          </Typography>
                        );
                      })()
                    ) : field === 'content' && originalLesson?.contentType === 'fullHtml' ? (
                      (() => {
                        return (
                          <Typography variant="body2" sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {originalValue}
                          </Typography>
                        );
                      })()
                    ) : field === 'content' && originalLesson?.contentType === 'video' ? (
                      (() => {
                        return (
                          <Typography variant="body2" sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {typeof originalValue === 'string' ? originalValue : JSON.stringify(originalValue, null, 2)}
                          </Typography>
                        );
                      })()
                    ) : field === 'videoUrl' ? (
                      (() => {
                        return (
                          <Typography variant="body2" sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {typeof originalValue === 'string' ? originalValue : JSON.stringify(originalValue, null, 2)}
                          </Typography>
                        );
                      })()
                    ) : field === 'contentBlocks' || field === 'resources' || field === 'practiceExercises' ? (
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {originalValue}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {originalValue}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                {/* Права частина: Прев'ю оригіналу (50%) */}
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin:changeRequestDetail.preview.howItLooks')}:
                    </Typography>
                    {(originalLesson?.contentType === 'presentation' || originalLesson?.contentType === 'fullHtml' || originalLesson?.contentType === 'htmlBlock') && (
                      <Tooltip title="Responsive Preview">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenResponsivePreview(
                            originalLesson,
                            `${t('admin:changeRequestDetail.preview.beforeChanges')} - ${t('admin:changeRequestDetail.preview.howItLooked')}`
                          )}
                          sx={{ color: 'primary.main' }}
                        >
                          <SmartphoneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: '16px',
                      bgcolor: (theme) => theme.palette.background.default,
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                      flex: 1,
                      minHeight: '300px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.5)',
                        },
                      },
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      {field === 'content' && (originalLesson?.contentType === 'presentation' || originalLesson?.contentType === 'htmlBlock' ) ? (
                        <div dangerouslySetInnerHTML={{ __html: originalValue || '' }} />
                      ) : field === 'content' && originalLesson?.contentType === 'fullHtml' ? (
                        <iframe
                          srcDoc={originalValue || ''}
                          title="Custom HTML Preview"
                          style={{
                            width: '100%',
                            height: '280px',
                            border: 'none',
                            borderRadius: '0',
                            backgroundColor: 'transparent',
                            overflow: 'auto'
                          }}
                          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                          scrolling="auto"
                        />
                      ) : field === 'content' && originalLesson?.contentType === 'video' ? (
                        renderVideoPreview(originalValue, '300px')
                      ) : field === 'videoUrl' ? (
                        renderVideoPreview(originalValue, '300px')
                      ) : (
                        <Typography color="text.secondary">
                  {originalValue}
                        </Typography>
                      )}
                    </Box>
                </Paper>
              </Grid>
              </Grid>
            </Box>
            
            {/* AFTER CHANGES - запропонований контент + прев'ю (50% + 50%) */}
            <Box>
              <Typography variant="h6" gutterBottom color="success.main" sx={{ mb: 2 }}>
                {t('admin:changeRequestDetail.preview.afterChanges')}
                </Typography>
              <Grid container spacing={2} alignItems="stretch">
                {/* Ліва частина: Запропонований контент (50%) */}
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin:changeRequestDetail.preview.proposedText')}:
                    </Typography>
                  </Box>
                <Paper 
                    elevation={0}
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                      borderRadius: '16px',
                      bgcolor: 'background.default',
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                      flex: 1,
                      minHeight: '300px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.5)',
                        },
                      },
                    }}
                  >
                    {field === 'content' && (changeRequest?.proposedChanges?.contentType === 'presentation' || changeRequest?.proposedChanges?.contentType === 'htmlBlock' ) ? (
                      (() => {
                        return (
                          <Typography variant="body2" sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            fontSize: '0.9rem',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {newValue}
                          </Typography>
                        );
                      })()
                    ) : field === 'contentBlocks' || field === 'resources' || field === 'practiceExercises' ? (
                      <Typography variant="body2" sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {newValue}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {newValue}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                {/* Права частина: Прев'ю запропонованих змін (50%) */}
                <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('admin:changeRequestDetail.preview.howItWillLook')}:
                    </Typography>
                    {(changeRequest?.proposedChanges?.contentType === 'presentation' || changeRequest?.proposedChanges?.contentType === 'fullHtml' || changeRequest?.proposedChanges?.contentType === 'htmlBlock') && (
                      <Tooltip title="Responsive Preview">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenResponsivePreview(
                            createPreviewLesson(),
                            `${t('admin:changeRequestDetail.preview.afterChanges')} - ${t('admin:changeRequestDetail.preview.howItWillLook')}`
                          )}
                          sx={{ color: 'success.main' }}
                        >
                          <SmartphoneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: '16px',
                      bgcolor: 'background.default',
                      display: 'flex',
                      flexDirection: 'column',
                      boxSizing: 'border-box',
                      flex: 1,
                      minHeight: '300px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.5)',
                        },
                      },
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      {field === 'content' && (changeRequest?.proposedChanges?.contentType === 'presentation' || changeRequest?.proposedChanges?.contentType === 'htmlBlock') ? (
                        <div dangerouslySetInnerHTML={{ __html: newValue || '' }} />
                      ) : field === 'content' && changeRequest?.proposedChanges?.contentType === 'fullHtml' ? (
                        (() => {
                          return (
                            <iframe
                              srcDoc={newValue || ''}
                              title="Full HTML Preview"
                              style={{
                                width: '100%',
                                height: '280px',
                                border: 'none',
                                borderRadius: '0',
                                backgroundColor: 'transparent',
                                overflow: 'auto'
                              }}
                              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                              scrolling="auto"
                            />
                          );
                        })()
                      ) : field === 'content' && changeRequest?.proposedChanges?.contentType === 'video' ? (
                        (() => {
                          return (
                            <Typography variant="body2" sx={{ 
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              fontSize: '0.9rem',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}>
                              {typeof newValue === 'string' ? newValue : JSON.stringify(newValue, null, 2)}
                            </Typography>
                          );
                        })()
                      ) : field === 'videoUrl' ? (
                        (() => {
                          return (
                            <Typography variant="body2" sx={{ 
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                              fontSize: '0.9rem',
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}>
                              {typeof newValue === 'string' ? newValue : JSON.stringify(newValue, null, 2)}
                            </Typography>
                          );
                        })()
                      ) : (
                        <Typography color="text.secondary">
                  {newValue}
                        </Typography>
                      )}
                    </Box>
                </Paper>
              </Grid>
            </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }, [getFieldValue, originalLesson, changeRequest?.proposedChanges, changeRequest?.status, changeRequest?.approvedFields, selectedFields, t, handleFieldToggle, renderContentBlocksDiff]);

  const isStatusPending = changeRequest?.status === 'pending';

  // Обработчик закрытия модального окна с сбросом состояния предварительного просмотра
  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  // Функція для відкриття responsive preview
  const handleOpenResponsivePreview = useCallback((lesson, title, block = null) => {
    setResponsivePreviewLesson(lesson);
    setResponsivePreviewTitle(title);
    setSelectedBlock(block);
    setResponsivePreviewOpen(true);
  }, []);

  // Функція для закриття responsive preview
  const handleCloseResponsivePreview = useCallback(() => {
    setResponsivePreviewOpen(false);
    setResponsivePreviewLesson(null);
    setResponsivePreviewTitle('');
    setSelectedBlock(null);
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            minHeight: '60vh'
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {t('admin:changeRequestDetail.title')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : !changeRequest ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('admin:changeRequestDetail.requestNotFound')}
            </Alert>
          ) : (
            <Box>
              <Paper sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('admin:changeRequestDetail.lesson')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {originalLesson?.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('admin:changeRequestDetail.status')}
                    </Typography>
                    <Chip
                      label={t(`admin:lessonChangeRequests.status.${changeRequest.status}`)}
                      color={
                        changeRequest.status === 'pending'
                          ? 'warning'
                          : changeRequest.status === 'approved'
                          ? 'success'
                          : 'error'
                      }
                      icon={
                        changeRequest.status === 'pending'
                          ? null
                          : changeRequest.status === 'approved'
                          ? <CheckCircleIcon />
                          : <CancelIcon />
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('admin:changeRequestDetail.requestedBy')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {changeRequest.sender?.fullName || changeRequest.requestedBy?.userMeta?.fullName || changeRequest.requestedBy?.fullName || t('common:unknownUser')}
                    </Typography>
                    {changeRequest.sender?.email && (
                      <Typography variant="body2" color="text.secondary">
                        {changeRequest.sender.email}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('admin:changeRequestDetail.createdAt')}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDate(changeRequest.createdAt)}
                    </Typography>
                  </Grid>
                  {changeRequest.processedBy && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t('admin:changeRequestDetail.processedBy')}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {changeRequest.processedBy?.userMeta?.fullName || changeRequest.processedBy?.fullName || t('common:unknownUser')}
                        </Typography>
                        {changeRequest.processedBy?.userMeta?.email && (
                          <Typography variant="body2" color="text.secondary">
                            {changeRequest.processedBy.userMeta.email}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t('admin:changeRequestDetail.processedAt')}
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {formatDate(changeRequest.processedAt)}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('admin:changeRequestDetail.userComment')}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                      <Typography variant="body1">
                        {changeRequest.comment || t('admin:changeRequestDetail.noComment')}
                      </Typography>
                    </Paper>
                  </Grid>
                  {changeRequest.adminComment && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('admin:changeRequestDetail.adminComment')}
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.paper' }}>
                        <Typography variant="body1">
                          {changeRequest.adminComment}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              <Box sx={{ mb: isStatusPending ? 4 : 0 }}>
                <Typography variant="h5" gutterBottom>
                  {t('admin:changeRequestDetail.changesTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isStatusPending 
                    ? t('admin:changeRequestDetail.selectFieldsMessage')
                    : t('admin:changeRequestDetail.changesOverview')}
                </Typography>



                {/* Field Selection - чекбокси для вибору полів для затвердження */}
                <Paper sx={{ p: 3, mt: 2, mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    {t('admin:changeRequestDetail.selectFieldsTitle')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    {t('admin:changeRequestDetail.selectFieldsDescription')}
                  </Typography>
                  
                  {changeRequest.modifiedFields && changeRequest.modifiedFields.length > 0 ? (
                    <Box>
                      {changeRequest.modifiedFields.map((field) => renderFieldDiff(field))}
                    </Box>
                  ) : (
                    <Typography variant="body1">
                      {t('admin:changeRequestDetail.noChangesFound')}
                    </Typography>
                  )}
                </Paper>                
              </Box>

              {/* Removed New Changes Section */}

              {isStatusPending && (
                <Box>
                  <Typography variant="h5" gutterBottom>
                    {t('admin:changeRequestDetail.adminResponseTitle')}
                  </Typography>
                  <TextField
                    label={t('admin:changeRequestDetail.adminCommentLabel')}
                    multiline
                    rows={4}
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    fullWidth
                    helperText={t('admin:changeRequestDetail.adminCommentHelperText')}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {!loading && !error && changeRequest && (
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button variant="text" onClick={handleClose}>
              {t('admin:changeRequestDetail.buttons.close')}
            </Button>
            {isStatusPending && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleRejectClick}
                  disabled={saving}
                >
                  {saving && confirmAction === 'reject'
                    ? t('admin:changeRequestDetail.buttons.processing')
                    : t('admin:changeRequestDetail.buttons.reject')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleApproveClick}
                  disabled={saving || selectedFields.length === 0}
                >
                  {saving && confirmAction === 'approve'
                    ? t('admin:changeRequestDetail.buttons.processing')
                    : t('admin:changeRequestDetail.buttons.approve')}
                </Button>
              </>
            )}
          </DialogActions>
        )}
      </Dialog>

      {/* Диалог подтверждения */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelDialog}
      >
        <DialogTitle>
          {confirmAction === 'approve'
            ? t('admin:changeRequestDetail.confirmApproveTitle')
            : t('admin:changeRequestDetail.confirmRejectTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmAction === 'approve'
              ? selectedFields.length > 0
                ? t('admin:changeRequestDetail.confirmApproveMessage', { count: selectedFields.length })
                : t('admin:changeRequestDetail.confirmApproveNoFieldsMessage')
              : t('admin:changeRequestDetail.confirmRejectMessage')}
          </Typography>
          {confirmAction === 'approve' && selectedFields.length > 0 && (
            <List dense>
              {selectedFields.map((field) => (
                <ListItem key={field}>
                  <ListItemText primary={t(`admin:changeRequestDetail.fields.${field}`)} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCancelDialog}>
            {t('admin:changeRequestDetail.buttons.cancel')}
          </Button>
          <Button
            onClick={handleProcessRequest}
            color={confirmAction === 'approve' ? 'success' : 'error'}
            variant="contained"
            disabled={confirmAction === 'approve' && selectedFields.length === 0}
          >
            {confirmAction === 'approve'
              ? t('admin:changeRequestDetail.buttons.confirmApprove')
              : t('admin:changeRequestDetail.buttons.confirmReject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Responsive Preview Modal */}
      <ResponsivePreviewModal
        open={responsivePreviewOpen}
        onClose={handleCloseResponsivePreview}
        lesson={responsivePreviewLesson}
        title={responsivePreviewTitle}
        selectedBlock={selectedBlock}
      />
    </>
  );
};

export default LessonChangeRequestModal; 